import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useSession } from '@/features/auth/use-session'
import type { Role } from '@/features/auth/types'

function SessionPending() {
  return (
    <div className="grid h-full place-items-center text-sm text-muted-foreground">
      Checking your session…
    </div>
  )
}

/** Blocks a route until the session cookie resolves to a staff member. */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { user, isLoading } = useSession()
  const location = useLocation()

  if (isLoading) return <SessionPending />
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />

  return <>{children ?? <Outlet />}</>
}

/**
 * Route-level role gate. UX only — the backend re-checks every call via
 * `RequireStaffRole` (app/session/middleware/auth.go), so a user who forges
 * their way past this still gets a 403 from the API.
 */
export function RequireRole({ allow, children }: { allow: Role[]; children?: ReactNode }) {
  const { user, isLoading } = useSession()

  if (isLoading) return <SessionPending />
  if (!user || !allow.includes(user.role)) return <Navigate to="/" replace />

  return <>{children ?? <Outlet />}</>
}
